import moment from 'jalali-moment'
import { DigitToMonth } from 'utilities/helper/digitToMonth'
import { fundTitle } from 'utilities/helper/format'
import { MoneyPoolTransactionTypes } from 'utilities/helper/moneyPools'

// «تراکنش‌های من»: every payment the member was on either end of, from every
// box they are in, wallet top-ups and plan upgrades included. One row is one
// `payment.Transaction`, so the words and the direction below are worked out
// from where the money went (`source` → `destination`) and whether the member
// was the one paying — the row itself carries no «in» or «out».

/**
 * Which way the money went, as the member would say it.
 *
 *  - `in`:     it reached their own bank account (a loan, a withdrawal).
 *  - `out`:    they paid it (an installment, a membership fee, an upgrade).
 *  - `wallet`: their own money moved into their Hamyan wallet.
 *  - `move`:   it moved without leaving or reaching their pocket — a manager
 *              paying another member out of the box, somebody else paying an
 *              installment on their behalf, a box settling to its own account.
 */
export type TMyTransactionKind = 'in' | 'out' | 'wallet' | 'move'

/** `payment.Transaction.source` / `.destination` — `utils/constants/choice.py` */
const GATEWAY = 'gtw'
const WALLET = 'hwt'
const HAMYAN_PLAN = 'hpl'
const BOX_BALANCE = 'hbb'
const BOX_BANK_ACCOUNT = 'bba'
const MEMBER_BANK_ACCOUNT = 'mba'
const CASH = 'csh'

/** `payment.Transaction.ctx_type` */
const CTX_MONEYPOOL = 'mnp'
const CTX_CYCLE = 'ccl'
const CTX_ORDER = 'ord'

const BOX_SIDES = [BOX_BALANCE, BOX_BANK_ACCOUNT]

const LOAN = 'ctl'
const CUSTOM_CASHOUT = 'ctc'

export function kindOf(
    item: IDashboardTransactionResult,
    me: number | null
): TMyTransactionKind {
    const { source, destination } = item
    if (destination === WALLET) return 'wallet'
    if (destination === MEMBER_BANK_ACCOUNT) {
        // The manager who pays a loan out is the payer of that row too, so
        // «I paid» says nothing here: only the member it reached received it.
        return item.receiver === me ? 'in' : 'move'
    }
    if (BOX_SIDES.includes(source) && BOX_SIDES.includes(destination))
        return 'move'
    if (destination === HAMYAN_PLAN) return 'out'
    if (BOX_SIDES.includes(destination))
        return item.payer === me ? 'out' : 'move'
    if (item.payer === me) return 'out'
    if (item.receiver === me) return 'in'
    return 'move'
}

export interface IMyTransactionView {
    kind: TMyTransactionKind
    /** «صندوق مهر», «کیف پول همیان», «ارتقای صندوق» */
    title: string
    /** what it was for: «پرداخت قسط», «شارژ کیف پول», «دریافت وام» */
    subtitle: string
    /** the other person in it, when there is one: «برای …», «به …», «پرداخت توسط …» */
    party: string
    /** the box's own page, when the row says which box */
    boxPath: string | null
}

const trimmed = (name: string | null | undefined) => (name ?? '').trim()

function moneypoolSubtitle(
    item: IDashboardTransactionResult,
    kind: TMyTransactionKind
): string {
    const type = item.moneypool_data?.type ?? ''
    if (kind === 'in') {
        if (type === LOAN) return 'دریافت وام'
        if (type === CUSTOM_CASHOUT) return 'برداشت از صندوق'
    }
    if (type && MoneyPoolTransactionTypes[type])
        return MoneyPoolTransactionTypes[type]
    // No cash-in or cash-out row behind it: the box's own money moving from
    // «موجودی نزد همیان» to the box's bank account.
    if (BOX_SIDES.includes(item.source) && BOX_SIDES.includes(item.destination))
        return 'انتقال به حساب صندوق'
    return item.moneypool_data?.is_cashin ? 'واریز به صندوق' : 'برداشت از صندوق'
}

function cycleSubtitle(
    item: IDashboardTransactionResult,
    kind: TMyTransactionKind
): string {
    if (item.destination === MEMBER_BANK_ACCOUNT)
        return kind === 'in' ? 'دریافت وام قرعه‌کشی' : 'پرداخت وام قرعه‌کشی'
    if (BOX_SIDES.includes(item.source) && BOX_SIDES.includes(item.destination))
        return 'انتقال به حساب صندوق'
    return 'پرداخت سهم'
}

function partyOf(
    item: IDashboardTransactionResult,
    kind: TMyTransactionKind,
    me: number | null
): string {
    const receiver = trimmed(item.receiver_name)
    const payer = trimmed(item.payer_name)
    if (item.destination === MEMBER_BANK_ACCOUNT) {
        return kind === 'move' && receiver ? `به ${receiver}` : ''
    }
    if (!BOX_SIDES.includes(item.destination)) return ''
    if (BOX_SIDES.includes(item.source)) return ''
    // Paid for somebody else, or by somebody else. The one who pays for a
    // whole family is exactly who needs to tell these rows apart.
    if (kind === 'out' && item.receiver !== null && item.receiver !== me)
        return receiver ? `برای ${receiver}` : ''
    if (kind === 'move' && item.payer !== me)
        return payer ? `پرداخت توسط ${payer}` : ''
    return ''
}

export function describeTransaction(
    item: IDashboardTransactionResult,
    me: number | null
): IMyTransactionView {
    const kind = kindOf(item, me)
    const party = partyOf(item, kind, me)

    if (item.destination === WALLET) {
        return {
            kind,
            title: 'کیف پول همیان',
            subtitle:
                item.source === GATEWAY ? 'شارژ کیف پول' : 'واریز به کیف پول',
            party: '',
            boxPath: null,
        }
    }
    if (item.ctx_type === CTX_ORDER || item.destination === HAMYAN_PLAN) {
        return {
            kind,
            title: 'ارتقای صندوق',
            subtitle:
                item.source === WALLET
                    ? 'پرداخت از کیف پول'
                    : item.source === GATEWAY
                    ? 'پرداخت با درگاه بانکی'
                    : 'خرید اشتراک',
            party: '',
            boxPath: null,
        }
    }
    if (item.ctx_type === CTX_MONEYPOOL && item.moneypool_data) {
        return {
            kind,
            title: fundTitle(item.moneypool_data.moneypool_name),
            subtitle: moneypoolSubtitle(item, kind),
            party,
            boxPath: `/fund/${item.moneypool_data.id}`,
        }
    }
    if (item.ctx_type === CTX_CYCLE && item.cycle_data) {
        const cashboxId = item.cycle_data.cashbox_id
        return {
            kind,
            title: fundTitle(item.cycle_data.cashbox_name),
            subtitle: cycleSubtitle(item, kind),
            party,
            // Only a server that sends the cashbox's id can link to it — a
            // cycle's own id is not a page anyone can open.
            boxPath:
                typeof cashboxId === 'number' ? `/cashbox/${cashboxId}` : null,
        }
    }
    return {
        kind,
        title: 'تراکنش همیان',
        subtitle: kind === 'in' ? 'دریافت' : kind === 'out' ? 'پرداخت' : '',
        party,
        boxPath: null,
    }
}

/** Whether the row belongs to a صندوق قرعه‌کشی rather than a قرض‌الحسنه. */
export const isCashboxRow = (item: IDashboardTransactionResult) =>
    item.ctx_type === CTX_CYCLE

/**
 * «از» / «به» on the details sheet, in the words the rest of the app uses for
 * the same places.
 */
export function placeLabel(
    place: string,
    item: IDashboardTransactionResult,
    me: number | null,
    side: 'from' | 'to'
): string {
    switch (place) {
        case GATEWAY:
            return 'درگاه بانکی'
        case WALLET:
            return 'کیف پول همیان'
        case BOX_BALANCE:
            return 'موجودی صندوق نزد همیان'
        case BOX_BANK_ACCOUNT:
            return 'حساب بانکی صندوق'
        case HAMYAN_PLAN:
            return 'همیان'
        case CASH:
            return 'نقدی'
        case MEMBER_BANK_ACCOUNT: {
            const owner = side === 'to' ? item.receiver : item.payer
            const name = trimmed(
                side === 'to' ? item.receiver_name : item.payer_name
            )
            if (owner === me) return 'حساب بانکی شما'
            return name ? `حساب بانکی ${name}` : 'حساب بانکی عضو'
        }
        default:
            return ''
    }
}

/**
 * What left the member's pocket: the amount and the commission charged on
 * top of it. Only for what they paid themselves — the commission on a loan
 * paid out to them is the box's, not theirs.
 */
export function chargedTotal(item: IDashboardTransactionResult): number {
    return (item.amount ?? 0) + (item.total_commission ?? 0)
}

// Iran dropped daylight saving in 1401, so Tehran is a fixed +03:30. The same
// constant as the fund tab's `days.ts`.
const TEHRAN_UTC_OFFSET_MINUTES = 210

/**
 * When it happened, on Tehran's clock. `state_time` is when the payment
 * settled — the moment the member saw it go through — and it arrives as
 * «2026-09-21 18:15:02+03:30»; `created` is the fallback for a row that never
 * got one.
 */
export function tehranMoment(
    item: Pick<IDashboardTransactionResult, 'state_time' | 'created'>
): moment.Moment | null {
    for (const stamp of [item.state_time, item.created]) {
        if (!stamp) continue
        const parsed = moment(stamp.replace(' ', 'T'), moment.ISO_8601)
        if (parsed.isValid()) return parsed.utcOffset(TEHRAN_UTC_OFFSET_MINUTES)
    }
    return null
}

/** «1405-06-30», the key `describeDay` reads — '' when there is no date. */
export function jalaliDayKey(
    item: Pick<IDashboardTransactionResult, 'state_time' | 'created'>
): string {
    const at = tehranMoment(item)
    return at ? at.format('jYYYY-jMM-jDD') : ''
}

/** «۱۸:۱۵», or «۱۸:۱۵:۰۲» for the details sheet. */
export function clockTime(
    item: Pick<IDashboardTransactionResult, 'state_time' | 'created'>,
    withSeconds = false
): string {
    const at = tehranMoment(item)
    return at ? at.format(withSeconds ? 'HH:mm:ss' : 'HH:mm') : ''
}

export interface ITransactionDay<T> {
    key: string
    rows: T[]
}

/**
 * Runs of the same day, in the order the server sent them. Runs, not buckets:
 * the list is newest-first by id, and re-sorting it here would quietly
 * disagree with the order it pages in.
 */
export function runsByDay<T extends IDashboardTransactionResult>(
    rows: T[]
): ITransactionDay<T>[] {
    const days: ITransactionDay<T>[] = []
    rows.forEach((row) => {
        const key = jalaliDayKey(row)
        const last = days[days.length - 1]
        if (last && last.key === key) last.rows.push(row)
        else days.push({ key, rows: [row] })
    })
    return days
}

// --- «بازهٔ زمانی» -----------------------------------------------------------

export type TPeriod =
    | ''
    | 'this_month'
    | 'last_month'
    | 'three_months'
    | 'this_year'

export const PERIOD_OPTIONS: { key: TPeriod; label: string; aria?: string }[] =
    [
        { key: '', label: 'همه', aria: 'همهٔ تراکنش‌ها' },
        { key: 'this_month', label: 'این ماه' },
        { key: 'last_month', label: 'ماه گذشته' },
        { key: 'three_months', label: 'سه ماه اخیر' },
        { key: 'this_year', label: 'امسال' },
    ]

export interface IPeriodRange {
    /** 'jYYYY/jMM/jDD', both ends included */
    from: string
    to: string
}

const RANGE_FORMAT = 'jYYYY/jMM/jDD'

function tehranToday(now: Date) {
    return moment(now).utcOffset(TEHRAN_UTC_OFFSET_MINUTES)
}

/**
 * The two Jalali days a period covers, both included. The server takes them
 * as `date=1405/06/01,1405/06/31` and reads the second one as the whole day.
 */
export function periodRange(
    period: TPeriod,
    now: Date = new Date()
): IPeriodRange | null {
    if (period === '') return null
    const today = tehranToday(now)
    const format = (at: moment.Moment) => at.format(RANGE_FORMAT)
    switch (period) {
        case 'this_month':
            return {
                from: format(today.clone().startOf('jMonth')),
                to: format(today),
            }
        case 'last_month': {
            const lastMonth = today.clone().startOf('jMonth').subtract(1, 'day')
            return {
                from: format(lastMonth.clone().startOf('jMonth')),
                to: format(lastMonth),
            }
        }
        case 'three_months':
            return {
                from: format(today.clone().subtract(3, 'jMonth').add(1, 'day')),
                to: format(today),
            }
        case 'this_year':
            return {
                from: format(today.clone().startOf('jYear')),
                to: format(today),
            }
        default:
            return null
    }
}

/** The `date=` the server expects, or undefined for «همه». */
export function periodParam(
    period: TPeriod,
    now: Date = new Date()
): string | undefined {
    const range = periodRange(period, now)
    return range ? `${range.from},${range.to}` : undefined
}

/**
 * «۱ شهریور تا ۲۶ شهریور ۱۴۰۵» — the year said once when both ends share it,
 * which every period but «سه ماه اخیر» in spring does.
 */
export function periodRangeLabel(range: IPeriodRange): string {
    const from = moment(range.from, RANGE_FORMAT)
    const to = moment(range.to, RANGE_FORMAT)
    if (!from.isValid() || !to.isValid()) return ''
    const day = (at: moment.Moment) =>
        `${at.jDate()} ${DigitToMonth[at.jMonth() + 1]}`
    return from.jYear() === to.jYear()
        ? `${day(from)} تا ${day(to)} ${to.jYear()}`
        : `${day(from)} ${from.jYear()} تا ${day(to)} ${to.jYear()}`
}

// --- the period's totals -----------------------------------------------------

export interface IPeriodTotals {
    received: number
    paid: number
    wallet: number
    count: number
}

/**
 * What came in, what went out and what was topped up, over the rows given.
 * «پرداختی» counts what left the member's pocket — the commission included —
 * because that is the figure a bank statement will show them.
 */
export function periodTotals(
    rows: IDashboardTransactionResult[],
    me: number | null
): IPeriodTotals {
    return rows.reduce<IPeriodTotals>(
        (totals, row) => {
            const kind = kindOf(row, me)
            if (kind === 'in') totals.received += row.amount ?? 0
            else if (kind === 'out') totals.paid += chargedTotal(row)
            else if (kind === 'wallet') totals.wallet += row.amount ?? 0
            totals.count += 1
            return totals
        },
        { received: 0, paid: 0, wallet: 0, count: 0 }
    )
}
